import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Activity, ChevronDown, Plus } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

export const HEALTH = [
  ["treatment", "تحت العلاج", "amber"], ["hospital", "في المستشفى (منوّم)", "red"], ["sick_leave", "إجازة مرضية", "orange"],
  ["surgery", "عملية جراحية", "rose"], ["physio", "علاج طبيعي", "sky"], ["temp_partial", "عجز جزئي مؤقت", "violet"],
  ["perm_partial", "عجز جزئي دائم", "purple"], ["total", "عجز كلي", "zinc"], ["recovered", "شفاء تام", "emerald"],
  ["returned", "عاد للعمل", "teal"], ["deceased", "وفاة", "slate"],
];
const CLS = {
  amber: "bg-amber-50 text-amber-700 border-amber-200", red: "bg-red-50 text-red-700 border-red-200", orange: "bg-orange-50 text-orange-700 border-orange-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200", sky: "bg-sky-50 text-sky-700 border-sky-200", violet: "bg-violet-50 text-violet-700 border-violet-200",
  purple: "bg-purple-50 text-purple-700 border-purple-200", zinc: "bg-zinc-200 text-zinc-800 border-zinc-300", emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200", slate: "bg-slate-700 text-white border-slate-700",
};
const DIS = new Set(["temp_partial", "perm_partial", "total"]);

export const HealthBadge = ({ h, testid }) => (h ? (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${CLS[h.color] || CLS.amber}`} data-testid={testid}>
    <Activity className="h-3 w-3" />{h.label}{h.disability_pct != null ? ` ${h.disability_pct}%` : ""}</span>) : null);

export const HealthControl = ({ inj, canEdit, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ status: inj.health?.key || "treatment", note: "", date: new Date().toISOString().slice(0, 10), disability_pct: inj.health?.disability_pct ?? "", committee_date: inj.health?.committee_date || "" }); }, [open, inj]);
  const save = async () => {
    setBusy(true);
    try { await api.setInjuryHealth(inj.id, f); toast.success("تم تحديث حالة الموظف"); setOpen(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const hist = (inj.health_history || []).slice(-6).reverse();
  return (
    <>
      <button type="button" disabled={!canEdit} onClick={() => setOpen(true)} data-testid="inj-health-btn" className="inline-flex items-center gap-0.5">
        {inj.health ? <HealthBadge h={inj.health} testid="inj-health-badge" />
          : <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] text-slate-500"><Plus className="h-3 w-3" />حالة الموظف</span>}
        {canEdit && <ChevronDown className="h-3 w-3 text-slate-400" />}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-md" data-testid="inj-health-dialog">
          <DialogHeader className="pe-6 text-right"><DialogTitle className="font-heading">الحالة الصحية للموظف</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-1.5">
            {HEALTH.map(([k, l, c]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, status: k })} data-testid={`inj-health-opt-${k}`}
                className={`rounded-xl border px-3 py-2 text-right text-sm transition-colors ${f.status === k ? `${CLS[c]} font-bold` : "border-slate-200 hover:bg-slate-50"}`}>{l}</button>))}
          </div>
          {DIS.has(f.status) && (
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-violet-200 bg-violet-50/50 p-3">
              <div><Label className="text-xs">نسبة العجز %</Label><Input type="number" min={0} max={100} value={f.disability_pct} onChange={(e) => setF({ ...f, disability_pct: e.target.value })} className="mt-1 h-9 bg-white" data-testid="inj-health-pct" /></div>
              <div><Label className="text-xs">تاريخ تقرير اللجنة الطبية</Label><Input type="date" value={f.committee_date} onChange={(e) => setF({ ...f, committee_date: e.target.value })} className="mt-1 h-9 bg-white" data-testid="inj-health-committee" /></div>
            </div>)}
          <div className="space-y-2">
            <div><Label className="text-xs">التاريخ</Label><Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1 h-9 w-48" data-testid="inj-health-date" /></div>
            <div><Label className="text-xs">ملاحظة (اختيارية)</Label><Textarea rows={2} value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1" data-testid="inj-health-note" /></div>
          </div>
          {hist.length > 0 && (
            <div className="rounded-xl bg-slate-50 p-2.5 text-[11px] text-slate-600" data-testid="inj-health-history">
              <p className="mb-1 font-bold text-slate-700">سجل الحالة</p>
              {hist.map((h, i) => <p key={i}>• {h.date} — <b>{h.label}</b>{h.disability_pct != null ? ` ${h.disability_pct}%` : ""} — {h.by}{h.note ? ` · ${h.note}` : ""}</p>)}
            </div>)}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} disabled={busy} data-testid="inj-health-save">{busy && <Loader2 className="me-1 h-4 w-4 animate-spin" />} حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const AddDocButton = ({ inj, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!label.trim()) return toast.error("اكتب اسم المستند");
    setBusy(true);
    try { await api.addInjuryCategory(inj.id, label.trim()); setLabel(""); setOpen(false); onAdded(); toast.success("تمت إضافة المستند — ارفع ملفاته"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الإضافة"); } finally { setBusy(false); }
  };
  return open ? (
    <div className="flex items-center gap-2 rounded-xl border-2 border-dashed border-rose-200 p-3 sm:col-span-2" data-testid="inj-add-doc-form">
      <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="اسم المستند: تقرير أشعة، إجازة مرضية…" className="h-9" data-testid="inj-add-doc-name" />
      <Button size="sm" onClick={add} disabled={busy} className="h-9 bg-rose-600 hover:bg-rose-700" data-testid="inj-add-doc-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "إضافة"}</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-9">إلغاء</Button>
    </div>
  ) : (
    <button type="button" onClick={() => setOpen(true)} data-testid="inj-add-doc-btn"
      className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-200 p-4 text-sm font-semibold text-slate-500 transition-colors hover:border-rose-300 hover:text-rose-600 sm:col-span-2">
      <Plus className="h-4 w-4" /> إضافة مستند آخر</button>
  );
};
